<template>
    <div class="content-with-gallery">
        <div class="gallery-carousel">
            <div class="carousel">
                <div
                    ref="container"
                    class="slides"
                >
                    <div
                        v-for="(galleryItem, index) in data.gallery"
                        :key="index"
                        ref="elements"
                        class="slide"
                    >
                        <CmsImage
                            :data="galleryItem.image"
                            fit="cover"
                            :is-eager="hasEagerImage && !index"
                        />
                    </div>
                </div>
            </div>

            <ButtonRound
                :disabled="isAtStart"
                class="left"
                icon="angle-left"
                :aria-label="t('catalogue.carousel.button_prev')"
                @click="previous"
            />

            <ButtonRound
                :disabled="isAtEnd"
                class="right"
                icon="angle-right"
                :aria-label="t('catalogue.carousel.button_next')"
                @click="next"
            />
        </div>

        <div class="text-content">
            <ContentComponentItem
                v-for="(component, key) in data.content"
                :key="key"
                :background="background"
                :data="component"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { useCarousel } from '@/components/layout/carousels/base/useCarouselNew'

import type { Color, ComponentContentWithGalleryFragment } from '@/graphql/api'

import ContentComponentItem from '@/components/cms/component/ContentComponentItem.vue'
import CmsImage from '@/components/cms/CmsImage.vue'
import ButtonRound from '@/components/ui/buttons/ButtonRound.vue'

const { data } = defineProps<{
    data: ComponentContentWithGalleryFragment
    background: Color
    hasEagerImage?: boolean
}>()

const { t } = useI18n()

const { container, elements, previous, next, isAtStart, isAtEnd } = useCarousel(
    computed(() => data.gallery.length)
)
</script>

<style lang="scss" scoped>
.content-with-gallery {
    width: 100%;
    max-width: 1440px;
    margin: 0 auto;

    @include min-width(768) {
        display: flex;
        flex-direction: row-reverse;
        align-items: center;
    }

    .text-content {
        width: 100%;
        margin: 0 auto;
        padding: 40px 20px 10px;

        @include min-width(768) {
            max-width: 50%;
        }

        @include min-width(1024) {
            max-width: 460px;
        }
    }

    .gallery-carousel {
        width: 100%;
        line-height: 0;
        position: relative;

        @include min-width(768) {
            width: 100%;
            max-width: 50%;
            align-self: stretch;
        }

        .carousel {
            width: 100%;
            height: auto;

            .slides {
                position: relative;
                // width: 100%;
                display: flex;
                overflow-y: hidden;
                overflow-x: scroll;
                scroll-snap-type: x mandatory;
                scroll-padding: 0;
                padding: 0;
                -ms-overflow-style: none; /* IE and Edge */
                scrollbar-width: none; /* Firefox */

                &::-webkit-scrollbar {
                    display: none;
                }

                &::after {
                    content: '';
                    padding-right: 0;
                }

                @include min-width(1360) {
                    padding: 0;
                    scroll-padding: 0;
                }

                .slide {
                    position: relative;
                    user-select: none;
                    scroll-snap-align: start;
                    width: 100%;
                    flex-shrink: 0;

                    &:deep(.text-with-image.rounded-image) {
                        .image {
                            width: 220px;
                            height: 320px;
                            border-radius: 110px;

                            @include min-width(768) {
                                width: 335px;
                                height: 500px;
                                border-radius: 200px;
                            }
                        }
                    }
                }
            }
        }

        .button-round {
            position: absolute;
            z-index: 3;
            top: calc(50% - 25px);

            &.disabled {
                opacity: 0;
            }

            &.left {
                left: 15px;

                @include min-width(1600) {
                    left: -20px;
                }
            }

            &.right {
                right: 15px;

                @include min-width(1600) {
                    right: -20px;
                }
            }
        }
    }
}
</style>
